import React from 'react';
import { Card } from 'primereact/card';
import { Avatar } from 'primereact/avatar';
import { AvatarGroup } from 'primereact/avatargroup';
import { Button } from 'primereact/button';
import { SelectButton } from 'primereact/selectbutton';

import SectionCard from '../../../components/agentProfile/SectionCard';

import { initialsFrom, colorFor, subtitleFor } from '../utils';
import EmptyInline from '../EmptyInline';

/* ─── SuggestionCard (section-internal sub-component) ────────────────
   "People you may know" tile in the main column. Cover banner +
   overlapping avatar + dismiss × + name + clamped headline + (mutual
   face-pile / location) + full-width Connect button.

   Whole tile body (avatar+name+headline area) is keyboard-clickable —
   fires onView(id). Connect / Dismiss stop propagation so they don't
   double-fire navigation. */
const SuggestionCard = ({ suggestion, connections = [], actionLoading, onConnect, onDismiss, onView }) => {
    const id = suggestion.profile_id || suggestion.id;
    const color = colorFor(id);
    const mutualCount = suggestion.mutual_count || 0;
    const isLoading = actionLoading === id;

    /* Defensive photo URL — covers the field-name variations the suggestions
       endpoint may return depending on the backend joining strategy. */
    const photoUrl = suggestion.profile_photo_url_full
        || suggestion.profile_photo_url
        || suggestion.profile_photo
        || suggestion.avatar
        || suggestion.photo_url
        || null;

    // Face pile: prefer real mutual_profiles from the API; fall back to 3 of
    // the user's own connections as a visual placeholder so the LinkedIn
    // social-proof pattern stays consistent until the backend exposes
    // suggestion.mutual_profiles[].
    const faceProfiles = (suggestion.mutual_profiles && suggestion.mutual_profiles.length > 0)
        ? suggestion.mutual_profiles.slice(0, 3)
        : connections.slice(0, 3).map((c) => c.other_party).filter(Boolean);

    /* Cover banner = PrimeReact Card `header` prop. Theme-primary gradient
       so it picks up the brand color rather than being locked to a fixed
       hex pair. */
    const coverHeader = (
        <div
            style={{
                height: '3.5rem',
                background: 'linear-gradient(135deg, var(--primary-500) 0%, var(--primary-700) 100%)',
            }}
        />
    );

    return (
        <div className="col-6 md:col-4 lg:col-3 p-2">
            <Card
                header={coverHeader}
                className="h-full relative overflow-hidden border-round-2xl"
                pt={{
                    root: { style: { borderRadius: '1rem' } },
                    body: { className: 'p-0' },
                    content: { className: 'p-0' },
                }}
            >
                {/* Dismiss × — overlaps the cover, white tint so it reads on
                    the gradient. */}
                <Button
                    icon="pi pi-times"
                    text
                    rounded
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onDismiss(id); }}
                    aria-label="Dismiss suggestion"
                    pt={{
                        root: {
                            style: {
                                position: 'absolute',
                                top: '0.25rem',
                                right: '0.25rem',
                                width: '1.75rem',
                                height: '1.75rem',
                                color: 'var(--surface-0)',
                                zIndex: 1,
                            },
                        },
                    }}
                />

                {/* Body — clickable area opens the agent's profile. Avatar
                    overlaps the cover via negative margin-top. */}
                <div
                    className="px-3 pb-3 text-center cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onClick={() => onView?.(id)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onView?.(id);
                        }
                    }}
                    style={{ marginTop: '-2rem' }}
                >
                    <Avatar
                        label={initialsFrom(suggestion.full_name)}
                        image={photoUrl}
                        shape="circle"
                        style={{
                            width: '4rem',
                            height: '4rem',
                            backgroundColor: `var(--${color}-100)`,
                            color: `var(--${color}-700)`,
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            border: '3px solid var(--surface-0)',
                            display: 'inline-flex',
                            marginBottom: '0.75rem',
                        }}
                    />

                    <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {suggestion.full_name || 'Unknown'}
                    </div>

                    {/* Headline — fixed minHeight so cards line up in the grid
                        even when one suggestion has a 1-line headline and
                        another has a 2-line one. */}
                    {(() => {
                        const subtitle = subtitleFor(suggestion);
                        return (
                            <div
                                className="text-600 text-xs line-height-3 mt-1"
                                style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    minHeight: '2.25rem',
                                }}
                            >
                                {subtitle || ' '}
                            </div>
                        );
                    })()}

                    {/* Mutual face pile + count, OR location, OR nothing —
                        in priority order so the slot stays single-line. */}
                    {mutualCount > 0 && faceProfiles.length > 0 ? (
                        <div className="flex align-items-center justify-content-center gap-2 mt-2">
                            <AvatarGroup>
                                {faceProfiles.map((m, i) => {
                                    const c = colorFor(m.profile_id || m.id);
                                    return (
                                        <Avatar
                                            key={m.profile_id || m.id || i}
                                            label={initialsFrom(m.full_name)}
                                            shape="circle"
                                            style={{
                                                width: '1.25rem',
                                                height: '1.25rem',
                                                backgroundColor: `var(--${c}-100)`,
                                                color: `var(--${c}-700)`,
                                                fontWeight: 700,
                                                fontSize: '0.5rem',
                                                border: '2px solid var(--surface-0)',
                                            }}
                                        />
                                    );
                                })}
                            </AvatarGroup>
                            <span className="text-500 text-xs">
                                <strong className="text-700">{mutualCount}</strong> mutual
                            </span>
                        </div>
                    ) : suggestion.location_city ? (
                        <div className="text-500 text-xs mt-2 flex align-items-center justify-content-center gap-1">
                            <i className="pi pi-map-marker" style={{ fontSize: '0.65rem' }} aria-hidden="true" />
                            {suggestion.location_city}
                        </div>
                    ) : null}
                </div>

                {/* Connect button — full width, anchored at bottom of card.
                    onClick stops propagation so it doesn't double-fire the
                    body's "view profile" click. */}
                <div className="px-3 pb-3" onClick={(e) => e.stopPropagation()}>
                    <Button
                        label="Connect"
                        icon="pi pi-user-plus"
                        size="small"
                        outlined
                        rounded
                        className="w-full"
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={(e) => { e.stopPropagation(); onConnect(id, suggestion.full_name); }}
                    />
                </div>
            </Card>
        </div>
    );
};

/**
 * PeopleYouMayKnowCard — 4-col grid of cover-banner suggestion tiles
 * with a SelectButton scope toggle ("In your org" / "Outside") at the
 * top. Sits between Pending Invitations and My Connections in the main
 * column, matching the reference's section order.
 *
 * Props:
 *   suggestions     — array of suggestion profiles (already filtered to
 *                     `visibleSuggestions` by parent — internal vs. external
 *                     scope + dismissed-id exclusion happens upstream)
 *   connections     — accepted connections list, used by SuggestionCard
 *                     for fallback face-pile when mutual_profiles is empty
 *   actionLoading   — id of the profile whose Connect/Dismiss button is busy
 *   suggestionScope — 'internal' | 'external'
 *   onScopeChange   — (scope: 'internal' | 'external') => void
 *   onConnect       — (id, fullName) => void
 *   onDismiss       — (id) => void
 *   onView          — (id) => void
 */
const PeopleYouMayKnowCard = ({
    suggestions,
    connections,
    actionLoading,
    suggestionScope,
    onScopeChange,
    onConnect,
    onDismiss,
    onView,
}) => (
    <div data-section="suggestions">
        <SectionCard
            icon="pi-sparkles"
            title="People you may know"
            subtitle="Based on your profile, connections, and activity"
            className="mb-3"
        >
            <div className="mb-3">
                <SelectButton
                    value={suggestionScope}
                    options={[
                        { label: 'In your org', value: 'internal' },
                        { label: 'Outside',     value: 'external' },
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => e.value && onScopeChange(e.value)}
                    allowEmpty={false}
                />
            </div>
            {suggestions.length === 0 ? (
                <EmptyInline
                    icon="pi-sparkles"
                    title="No suggestions"
                    description="Come back tomorrow for a fresh batch."
                />
            ) : (
                <div className="grid">
                    {suggestions.map((s) => (
                        <SuggestionCard
                            key={s.profile_id || s.id}
                            suggestion={s}
                            connections={connections}
                            actionLoading={actionLoading}
                            onConnect={onConnect}
                            onDismiss={onDismiss}
                            onView={onView}
                        />
                    ))}
                </div>
            )}
        </SectionCard>
    </div>
);

export default React.memo(PeopleYouMayKnowCard);
